import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const columns = [
  { label: 'Vendor Name', key: 'company_name' },
  { label: 'Company', key: 'company_id' },
  { label: 'PAN', key: 'pancard' },
  { label: 'GST Number', key: 'gst_number' },
  { label: 'Email', key: 'email' },
  { label: 'Advance Balance', key: 'advance_balance' },
  { label: 'Opening Balance', key: 'opening_balance' },
];

interface VendorType {
  id: number | string;
  company_name?: string;
  company_id?: number | string;
  opening_balance?: number | string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  email?: string;
  [key: string]: any;
}

interface CompanyType {
  id: number | string;
  company_name: string;
  [key: string]: any;
}

function decodeToken(token: string | undefined | null) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (_e) {
    return {};
  }
}

const Vendor: React.FC = () => {
  const [vendors, setVendors] = useState<VendorType[]>([]);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [statementRows, setStatementRows] = useState<any[]>([]);
  
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedVendorPurchases, setSelectedVendorPurchases] = useState<any[]>([]);
  const [selectedVendorName, setSelectedVendorName] = useState('');

  const [selectedVendorDetails, setSelectedVendorDetails] = useState<VendorType | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    let companyIdFromToken: string | null = null;
    if (token) {
      const decoded: any = decodeToken(token);
      if (decoded && (decoded.userId || decoded.company_id)) {
        companyIdFromToken = String(decoded.userId || decoded.company_id);
      }
    }
    setUserCompanyId(companyIdFromToken);

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [vendorsRes, companiesRes, rowsRes] = await Promise.all([
          axios.get(`${API_URL}/auth/vendors`),
          axios.get(`${API_URL}/auth/companies`),
          axios.get(`${API_URL}/auth/bank-statement-rows`),
        ]);
        setStatementRows(rowsRes.data || []);
        let vendorList = Array.isArray(vendorsRes.data) ? vendorsRes.data : [];
        if (companyIdFromToken) {
          const filtered = vendorList.filter(
            (v: VendorType) =>
              v.company_id !== undefined &&
              String(v.company_id) === companyIdFromToken
          );
          setVendors(filtered.length > 0 ? filtered : vendorList);
        } else {
          setVendors(vendorList);
        }
        setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      } catch (err) {
        setError('Error loading vendors or companies');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAdd = () => {
    navigate('/addvendor/?vendor_id=new');
  };

  const handleEdit = (id: number | string) => {
    navigate(`/addvendor/?vendor_id=${id}`);
  };

  const handleDelete = async (id: number | string) => {
    setDeleteError('');
    const confirmed = window.confirm('Are you sure you want to delete this vendor?');
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/auth/vendor/${id}`);
      setVendors((prev) => prev.filter((v) => v.id !== id));
    } catch (err) {
      setDeleteError('Error deleting vendor');
    } finally {
      setDeletingId(null);
    }
  };

  const getCompanyName = (company_id: number | string | undefined) => {
    if (!company_id) return <span className="text-gray-400">-</span>;
    const company = companies.find((c) => String(c.id) === String(company_id));
    return company ? company.company_name : <span className="text-gray-400">-</span>;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-blue-900">Vendors</h2>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition font-bold"
        >
          Add Vendor
        </button>
      </div>
      {error && <div className="text-red-600 mb-2 font-semibold">{error}</div>}
      {deleteError && <div className="text-red-600 mb-2 font-semibold">{deleteError}</div>}
      
      <div className="overflow-x-auto bg-white rounded-2xl shadow-xl border border-blue-100">
        <table className="min-w-full text-left text-sm border-collapse">
          <thead>
            <tr className="bg-blue-50 border-b border-blue-100">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="py-3 px-4 font-bold text-blue-900"
                >
                  {col.label}
                </th>
              ))}
              <th className="py-3 px-4 font-bold text-blue-900">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6 text-gray-500">Loading...</td>
              </tr>
            ) : vendors.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6 text-gray-400 italic">
                  No vendors found.
                </td>
              </tr>
            ) : (
              vendors.map((vendor) => (
                <tr key={vendor.id} className="hover:bg-blue-50/20 transition-colors">
                  {columns.map((col) => {
                    if (col.key === 'company_id') {
                      return (
                        <td key={col.key} className="py-3 px-4 text-slate-700">
                          {getCompanyName(vendor.company_id)}
                        </td>
                      );
                    }
                    if (col.key === 'advance_balance') {
                      const advanceAmt = statementRows
                        .filter(r => String(r.mapped_vendor_id) === String(vendor.id) && r.is_advance)
                        .reduce((sum, r) => sum + Number(r.debit || 0), 0);
                      return (
                        <td key={col.key} className="py-2 px-4 border-b text-sm font-bold text-amber-600">
                          ₹{advanceAmt.toFixed(2)}
                        </td>
                      );
                    }
                    if (col.key === 'opening_balance') {
                      return (
                        <td key={col.key} className="py-3 px-4 text-slate-900 font-bold">
                          ₹{vendor.opening_balance !== undefined && vendor.opening_balance !== null ? Number(vendor.opening_balance).toFixed(2) : '0.00'}
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} className="py-3 px-4 text-slate-700">
                        {vendor[col.key] || <span className="text-gray-400">-</span>}
                      </td>
                    );
                  })}
                  <td className="py-3 px-4 space-x-1">
                    <button
                      className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition text-xs font-semibold"
                      onClick={() => {
                        setSelectedVendorDetails(vendor);
                        setShowDetailsModal(true);
                      }}
                    >
                      More
                    </button>
                    <button
                      className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-xs font-semibold"
                      onClick={() => handleEdit(vendor.id)}
                      disabled={deletingId === vendor.id}
                    >
                      Edit
                    </button>
                    <button
                      className={`px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition text-xs font-semibold ${
                        deletingId === vendor.id ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                      onClick={() => handleDelete(vendor.id)}
                      disabled={deletingId === vendor.id}
                    >
                      {deletingId === vendor.id ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-xs font-semibold"
                      onClick={async () => {
                        setSelectedVendorName(vendor.company_name || 'Vendor');
                        try {
                          const billsRes = await axios.get(`${API_URL}/auth/purchase-invoices`);
                          const allBills = Array.isArray(billsRes.data) ? billsRes.data : [];
                          const vendorBills = allBills.filter((b: any) => String(b.Vendor_id) === String(vendor.id));
                          setSelectedVendorPurchases(vendorBills);
                          setShowPurchaseModal(true);
                        } catch (err) {
                          alert("Failed to load vendor purchase history.");
                        }
                      }}
                    >
                      Bills
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Details Drawer/Modal */}
      {showDetailsModal && selectedVendorDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full border border-slate-200 shadow-xl relative">
            <button
              onClick={() => {
                setShowDetailsModal(false);
                setSelectedVendorDetails(null);
              }}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 text-xl font-bold"
              style={{ background: 'none', border: 'none' }}
            >
              &times;
            </button>
            <h3 className="font-extrabold text-lg text-blue-900 mb-4 border-b pb-2">
              Vendor Profile: {selectedVendorDetails.company_name}
            </h3>
            <div className="space-y-3 text-sm text-slate-800">
              <div><strong>Parent Company:</strong> {getCompanyName(selectedVendorDetails.company_id)}</div>
              <div><strong>Email Address:</strong> {selectedVendorDetails.email || '—'}</div>
              <div><strong>PAN Card:</strong> {selectedVendorDetails.pancard || '—'}</div>
              <div><strong>GSTIN / UIN:</strong> {selectedVendorDetails.gst_number || '—'}</div>
              <div><strong>Opening Balance:</strong> ₹{Number(selectedVendorDetails.opening_balance || 0).toFixed(2)}</div>
              <div><strong>Advance Balance (Paid):</strong> ₹{(() => {
                const advanceAmt = statementRows
                  .filter(r => String(r.mapped_vendor_id) === String(selectedVendorDetails.id) && r.is_advance)
                  .reduce((sum, r) => sum + Number(r.debit || 0), 0);
                return advanceAmt.toFixed(2);
              })()}</div>
              <div className="border-t pt-3 mt-3">
                <strong>Registered Address:</strong>
                <p className="text-xs text-slate-600 mt-1">
                  {selectedVendorDetails.address || '—'}<br />
                  {selectedVendorDetails.city || '—'}, {selectedVendorDetails.state || '—'} - {selectedVendorDetails.country || '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchases History Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full border border-slate-200 shadow-xl relative">
            <button
              onClick={() => setShowPurchaseModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 text-xl font-bold"
              style={{ background: 'none', border: 'none' }}
            >
              &times;
            </button>
            <h3 className="font-extrabold text-lg text-blue-900 mb-4">
              Bills & Purchases History: {selectedVendorName}
            </h3>
            {selectedVendorPurchases.length === 0 ? (
              <p className="text-gray-500 text-sm py-4">No recorded purchase bills for this vendor yet.</p>
            ) : (
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="min-w-full text-left text-xs border-collapse">
                  <thead className="bg-blue-50 text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b">
                    <tr>
                      <th className="p-2.5">Bill / Invoice No</th>
                      <th className="p-2.5">Date</th>
                      <th className="p-2.5 text-right">TDS (₹)</th>
                      <th className="p-2.5 text-right">Total Amount (₹)</th>
                      <th className="p-2.5 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedVendorPurchases.map((b: any) => (
                      <tr key={b.id} className="hover:bg-blue-50/5">
                        <td className="p-2.5 font-bold text-slate-800">{b.InvoiceNo}</td>
                        <td className="p-2.5 text-gray-600">{new Date(b.Date).toLocaleDateString('en-GB')}</td>
                        <td className="p-2.5 text-right text-red-500">₹{Number(b.TDSAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td className="p-2.5 text-right font-extrabold text-slate-800">
                          ₹{Number(b.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="p-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${b.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {b.paid ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendor;
